import { FC, useState } from "react";
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import CloseSvg from "@/assets/svg/CloseSvg";

import { styles } from "./stylesReportModal";
import { useReportPostMutation } from "@/store/api/reportPostApi";
import CustomToast from "../../CustomToast/CustomToast";
import LeftArrow from "@/assets/svg/LeftArrow";

interface IReportPostModalProps {
  postUid: string;
  open: boolean;
  handleClose: () => void;
  variant: string;
}

const ReportModal: FC<IReportPostModalProps> = ({ open, handleClose, postUid, variant }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const [sendReport] = useReportPostMutation();

  const reasons = [
    { label: "Suspicious post", value: "SUSPICIOUS_POST" },
    { label: "Potential bot", value: "POTENTIAL_BOT" },
    { label: "Inaccurate data", value: "INACCURATE_DATA" },
    { label: "Other...", value: "OTHER" },
  ];

  const reasonsComment = [
    { label: "Suspicious comment", value: "SUSPICIOUS_POST" },
    { label: "Potential bot", value: "POTENTIAL_BOT" },
    { label: "Inaccurate data", value: "INACCURATE_DATA" },
    { label: "Other...", value: "OTHER" },
  ];

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value);
  };

  const handleClickSendReport = async () => {
    if (!navigator.onLine) {
      setToast({
        message: "No internet connection. Please check your network and try again.",
        success: false,
      });
      return;
    }

    try {
      handleClose();
      await sendReport({
        ...(variant === "post" ? { postUid } : { commentUid: postUid }),
        reason: selectedReason,
        detail: reasonText,
      }).unwrap();
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Failed to send report. Please try again.";
      setToast({
        message: errorMessage,
        success: false,
      });
      console.error("error", error);
    }
  };

  const handleCloseModal = () => {
    handleClose();
    setSelectedReason("");
    setReasonText("");
    setStep(1);
  };

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}

      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styles.modal}>
          <Box sx={styles.navigation}>
            {step === 2 && (
              <IconButton
                onClick={() => setStep(1)}
                size="small"
                aria-label="back"
                sx={{ ...styles.navigationIcon, left: "1.25rem" }}
              >
                <LeftArrow />
              </IconButton>
            )}
            <DialogTitle sx={styles.title}>
              {step === 1 ? "Please choose a reason:" : "Please tell us more:"}
            </DialogTitle>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              aria-label="close"
              sx={{ ...styles.navigationIcon, right: "1.25rem" }}
            >
              <CloseSvg />
            </IconButton>
          </Box>

          <Box sx={styles.content}>
            {step === 1 && (
              <>
                <Box>
                  <DialogContent
                    sx={{
                      overflow: "hidden",
                      marginRight: "-0.5rem",
                      padding: 0,
                    }}
                  >
                    <RadioGroup value={selectedReason} onChange={handleReasonChange}>
                      {variant === "post" &&
                        reasons.map((reason) => (
                          <FormControlLabel
                            labelPlacement="start"
                            key={reason.value}
                            value={reason.value}
                            sx={styles.reasonValueInput}
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  opacity: reason.value === selectedReason ? 1 : 0,
                                  "&.Mui-checked": {
                                    color: "#1976d2",
                                  },
                                }}
                              />
                            }
                            label={reason.label}
                          />
                        ))}
                      {variant === "comment" &&
                        reasonsComment.map((reason) => (
                          <FormControlLabel
                            labelPlacement="start"
                            key={reason.value}
                            value={reason.value}
                            sx={styles.reasonValueInput}
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  opacity: reason.value === selectedReason ? 1 : 0,
                                  "&.Mui-checked": {
                                    color: "#1976d2",
                                  },
                                }}
                              />
                            }
                            label={reason.label}
                          />
                        ))}
                    </RadioGroup>
                  </DialogContent>
                </Box>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedReason}
                  sx={styles.nextStepButton}
                >
                  Continue
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <TextField
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Tell something..."
                  sx={styles.reasonInput}
                  InputProps={{
                    sx: {
                      "&::placeholder": {
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "16px",
                        lineHeight: "150%",
                        letterSpacing: "0%",
                        color: "#C6CACE",
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Button
                  disabled={!reasonText.length}
                  sx={styles.nextStepButton}
                  onClick={handleClickSendReport}
                >
                  Send a report
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReportModal;
